import { useTranslation } from "react-i18next";
import { toNumber } from "lodash";
import { IconName } from "@/shared/ui/Icon";
import { useResendInvite, useContactInvite } from "@/features/people";
import { useRemoveCandidateFromPosition } from "@/features/projects/hooks/useRemoveCandidate";
import { useCreateAssessment } from "@/features/assessments/hooks/useCreateAssessment";
import { useToast } from "@/shared/contexts/toast.context";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ModalFileName } from "@/shared/modals";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import { useAssessmentsList } from "@/features/assessments/hooks/useAssessmentsList";
import { PersonRole } from "@/features/people/graphql/generated";
import { RoleType } from "@/shared/constants";
import { ResourceEnum } from "@/shared/constants/resources";
import { getPersonRoleObject } from "@/shared/utils/selectors/getRolesAndAccess";

interface UseCandidateItemActionsParams {
  projectId: number;
  scorecardId?: number | null;
  assessments?: any[];
}

export function useCandidateItemActions({
  projectId,
  scorecardId,
}: UseCandidateItemActionsParams) {
  const { t } = useTranslation();
  const navigate = useOrgNavigate();
  const { showToast } = useToast();
  const [openConfirmModal] = useModal(ModalFileName.CONFIRM);

  const { assessments } = useAssessmentsList({
    scorecardId,
  });

  const { handleResendInvite } = useResendInvite();
  const { handleSendContactInvite } = useContactInvite();
  const { handleRemoveCandidateFromPosition } =
    useRemoveCandidateFromPosition(projectId);
  const { handleCreateAssessment } = useCreateAssessment();

  const onResendInvite = (personId: number) => {
    handleResendInvite({ personId });
  };

  const onSendContactInvite = (personId: number) => {
    handleSendContactInvite(personId, projectId);
  };

  const onStartEvaluate = async (item: any) => {
    let assessment = assessments.find(
      (a) => a?.person?.id === toNumber(item.id)
    );
    let assessmentId = assessment?.id;

    if (!assessment) {
      assessment = await handleCreateAssessment({
        personId: toNumber(item.id),
        scorecardId: toNumber(scorecardId),
        projectId: toNumber(projectId),
      });
      assessmentId = assessment?.id;
    }

    navigate(
      `projects/${projectId}/person/${item.id}/assessment/${assessmentId}`
    );
  };

  const onCopyLink = (item: any) => {
    navigator.clipboard.writeText(
      `${import.meta.env.VITE_APP_URL}/invite/${item.invite?.secret}`
    );
    showToast({
      message: t("profile.link_copied"),
      type: "success",
    });
  };

  const onRemoveCandidate = (item: any) => {
    const name = item.name || item.email;
    const projectRole = getPersonRoleObject(
      (item?.roles || []) as PersonRole[],
      ResourceEnum.project,
      projectId
    );

    // TA-145: a candidate can also hold project access. Removing them from the board
    // must not silently revoke that access — it may be the viewer's own access, which
    // would lock them out of their own project. Let the remover choose.
    const isCandidateWithAccess =
      projectRole?.role === RoleType.candidate && !!projectRole?.access_level;

    if (isCandidateWithAccess) {
      openConfirmModal({
        title: t("hiring.confirm_remove_candidate_with_access", { name }),
        description: t("hiring.confirm_remove_candidate_with_access_hint"),
        // Least destructive option first: end candidacy, keep their access.
        confirmLabel: t("hiring.remove_candidate_keep_access"),
        confirmButtonVariant: "default",
        onConfirm: () =>
          handleRemoveCandidateFromPosition(item.id as number, true),
        secondaryLabel: t("hiring.remove_candidate_and_access"),
        secondaryButtonVariant: "destructive",
        onSecondary: () =>
          handleRemoveCandidateFromPosition(item.id as number, false),
        cancelLabel: t("common.cancel_remove"),
      });
      return;
    }

    openConfirmModal({
      onConfirm: () => handleRemoveCandidateFromPosition(item.id as number),
      title: t("hiring.confirm_remove_candidate", { name }),
      confirmLabel: t("common.confirm_remove"),
      cancelLabel: t("common.cancel_remove"),
      confirmButtonVariant: "destructive",
    });
  };

  const getItemActions = (item: any) => {
    const actions = [
      {
        label: t("user.resend_invite"),
        icon: "Refresh" as IconName,
        disabled: !item.invite,
        onClick: () => onResendInvite(item.id),
      },
      {
        label: t("people.send_invite"),
        icon: "EmailEmpty" as IconName,
        disabled: !item.is_contact,
        onClick: () => onSendContactInvite(item.id),
      },
      {
        label: t("projects.evaluate_candidate"),
        onClick: () => onStartEvaluate(item),
        icon: "ToolsAssesment" as IconName,
      },
      {
        label: t("profile.copy_link"),
        onClick: () => onCopyLink(item),
        icon: "Link" as IconName,
        disabled: !item.invite?.secret,
      },
      {
        label: t("hiring.remove_candidate"),
        onClick: () => onRemoveCandidate(item),
        icon: "Close" as IconName,
      },
    ];

    return actions.filter((action) => !action.disabled);
  };

  return { getItemActions, onRemoveCandidate };
}
